<template>
  <v-dialog v-model="open" max-width="900px" persistent>
    <template v-slot:activator="{ on, attrs }">
      <location-info-item
        icon="mdi-map-marker"
        icon-action="mdi-pencil-outline"
        title="Endereço"
        :subtitle="
          location.data && location.data.address
            ? location.data.address.full_address
            : formData.address.address_line_one
        "
        v-bind="attrs"
        v-on="on"
      />
    </template>

    <v-card>
      <v-toolbar elevation="0">
        <v-card-title class="ml-0 pl-1">
          Local da empresa
        </v-card-title>

        <v-spacer />

        <v-btn icon @click="open = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        Adicione um endereço para que os clientes possam ver no Google a
        localização da sua empresa. Se você não tem um local, como uma loja
        física ou um escritório, deixe esse campo em branco.
      </v-card-text>

      <v-row no-gutters>
        <v-col md="6">
          <v-card-text>
            <v-form ref="form" v-model="formValid" @submit="e => e.preventDefault()">
              <v-row no-gutters class="ml-2">
                <my-autocomplete
                    class="mb-6"
                    style="width: 100%"
                    label="Endereço"
                    :value="formData.address.address_line_one"
                    :callback="callbackGeocode"
                    @click="item => changeGeocodeAddress(item)"
                    @change="val => formData.address.address_line_one = val"
                    v-on:keyup.enter="() => callAction()"
                />
              </v-row>

              <v-row no-gutters class="ml-2">
                <v-text-field
                    label="Complemento"
                    v-model="formData.address.address_line_two"
                    required
                    outlined
                    v-on:keyup.enter="() => callAction()"
                ></v-text-field>
              </v-row>

              <v-row no-gutters class="ml-2">
                <v-text-field
                    label="Bairro"
                    v-model="formData.address.sublocality"
                    required
                    outlined
                    v-on:keyup.enter="() => callAction()"
                ></v-text-field>
              </v-row>

              <v-row no-gutters class="ml-2">
                <v-text-field
                  label="Cidade"
                  v-model="formData.address.locality"
                  required
                  outlined
                  v-on:keyup.enter="() => callAction()"
                ></v-text-field>
              </v-row>

              <v-row no-gutters class="ml-2">
                <v-col md="6" class="pr-2">
                  <v-text-field
                    label="Estado"
                    v-model="formData.address.administrative_area"
                    required
                    outlined
                    v-on:keyup.enter="() => callAction()"
                  ></v-text-field>
                </v-col>
                <v-col md="6" class="pl-2">
                  <v-text-field
                    label="Cep"
                    v-mask="'#####-###'"
                    v-model="formData.address.postal_code"
                    required
                    outlined
                    v-on:keyup.enter="() => callAction()"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-alert v-if="removeAddress" type="info" class="mt-2 ml-2">
                Ao remover o endereço você oculta a localização da sua empresa
                no Google. Os clientes nas suas áreas de cobertura ainda poderão
                pesquisar e encontrar sua empresa. Insira um endereço para
                mostrar sua localização no mapa.
              </v-alert>

              <v-alert
                v-if="alertScheduledTask"
                dense
                border="right"
                type="warning"
              >
                Existe um agendamento pendente para essa informação
              </v-alert>

              <v-row v-else no-gutters>
                <v-btn small color="primary" text @click="clearForm">
                  Remover endereço
                </v-btn>
              </v-row>

              <schedule-form v-if="!bulkUpdate" small v-on:change="changeScheduleDate" />
            </v-form>
          </v-card-text>
        </v-col>

        <v-col md="6" class="pl-1 pr-4 pt-4">
          <GmapMap
            :center="{ lat: formData.latitude, lng: formData.longitude }"
            :zoom="mapZoom"
            style="width: 100%; height: 315px"
            :options="{ streetViewControl: false }"
          >
            <GmapMarker
              v-if="!removeAddress"
              :position="{ lat: formData.latitude, lng: formData.longitude }"
              :clickable="true"
              :draggable="true"
              @dragend="onMarkerDragged"
            />
          </GmapMap>
        </v-col>
      </v-row>

      <v-divider />

      <v-card-actions fixed>
        <v-spacer></v-spacer>
        <v-btn color="secondary" text @click="cancel">
          Cancelar
        </v-btn>
        <v-btn :loading="loading" color="primary" text @click="callAction">
          Aplicar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar v-model="snackbar.show" timeout="3000" :color="snackbar.color">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Ok
        </v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import LocationInfoItem from '@/views/Locations/Components/LocationInfoItem';
import { get } from 'lodash';
import { mapState } from 'vuex';
import { gmapApi } from 'vue2-google-maps';
import { TYPE_UPDATE_LOCATION_ADDRESS } from '@/constants/tasks';
import ScheduleForm from '@/views/Locations/Components/ScheduleForm';
import AutoComplete from '@/components/AutoComplete';

export default {
  name: 'LocationAddressModal',
  props: {
    action: Function,
    bulkUpdate: {
      type: Boolean
    },
    alertScheduleTask: Array,
  },
  components: {
    LocationInfoItem,
    ScheduleForm,
    'my-autocomplete': AutoComplete
  },
  computed: {
    ...mapState({
      location: (state) => state.locations.location,
      geocodeAddresses: (state) => state.locations.geocodeAddresses,
      geocodeAddressesError: (state) => state.locations.geocodeAddressesError,
    }),
    google: gmapApi
  },
  mounted() {
    if(this.alertScheduleTask) {
      this.alertScheduleTask.map(type => {
        if(type === TYPE_UPDATE_LOCATION_ADDRESS) {
          this.alertScheduledTask = true;
        }
      })
    }
  },
  beforeDestroy() {
    this.resetData();
  },
  data: () => ({
    loadingGeocode: false,
    place: {},
    mapZoom: 12,
    open: false,
    loading: false,
    formValid: true,
    removeAddress: false,
    formDataOriginal: {
      address: {
        address_line_one: '',
        address_line_two: '',
        administrative_area: '',
        locality: '',
        postal_code: '',
        sublocality: ''
      },
      latitude: 0,
      longitude: 0
    },
    formData: {
      address: {
        address_line_one: '',
        address_line_two: '',
        administrative_area: '',
        locality: '',
        postal_code: '',
        sublocality: ''
      },
      schedule_type: TYPE_UPDATE_LOCATION_ADDRESS,
      schedule_start: '',
      schedule_start_at: '',
      schedule_end: '',
      schedule_end_at: '',
      latitude: 0,
      longitude: 0
    },
    snackbar: {
      text: '',
      show: false,
      color: 'info'
    },
    timeoutId: null,
    alertScheduledTask: null
  }),
  methods: {
    changeGeocodeAddress(value) {
      this.updateMap(value.lat, value.lng, value.address)
    },
    setData(location) {
      this.formData.address = {
        address_line_one: get(location, 'data.address.address_line_one'),
        address_line_two: get(location, 'data.address.address_line_two'),
        administrative_area: get(location, 'data.address.administrative_area'),
        locality: get(location, 'data.address.locality'),
        postal_code: get(location, 'data.address.postal_code'),
        sublocality: get(location, 'data.address.sublocality')
      };
      const lat = get(location, 'data.latitude', 0);
      const lng = get(location, 'data.longitude', 0);
      this.setLatLng(lat, lng);

      const { address_line_one, address_line_two, administrative_area, locality, postal_code, sublocality } = get(location, 'data.address', {})
      this.formDataOriginal.address = {
        address_line_one,
        address_line_two,
        administrative_area,
        locality,
        postal_code,
        sublocality
      };
      this.formDataOriginal.latitude = lat;
      this.formDataOriginal.longitude = lng;
    },
    cancel() {
      this.open = false;
      this.resetData();
    },
    resetData() {
      const { address_line_one, address_line_two, administrative_area, locality, postal_code, sublocality } = this.formDataOriginal.address
      const { latitude, longitude } = this.formDataOriginal

      this.formData.address = {
        address_line_one,
        address_line_two,
        administrative_area,
        locality,
        postal_code,
        sublocality
      };
      this.setLatLng(latitude, longitude)
      if (this.bulkUpdate) {
        this.$emit('removePayload', 'address')
      }
    },
    callAction: async function() {
      if (!this.$refs.form.validate()) {
        return
      }

      if (this.bulkUpdate) {
        let payload = {
          address_line_one: this.formData.address.address_line_one,
          address_line_two: this.formData.address.address_line_two,
          administrative_area: this.formData.address.administrative_area,
          locality: this.formData.address.locality,
          postal_code: this.formData.address.postal_code,
          sublocality: this.formData.address.sublocality
        };
        this.$emit('savePayload', 'address' , payload);
      } else if (this.action) {
        this.loading = true;
        await this.action(this.formData);
        this.loading = false;
      }

      this.open = false;
    },
    clearForm() {
      this.formData.address = {
        address: '',
        address_line_one: '',
        address_line_two: '',
        administrative_area: '',
        locality: '',
        postal_code: '',
        sublocality: ''
      };
      this.removeAddress = true;
    },
    onMarkerDragged(event) {
      this.setLatLng(event.latLng.lat(), event.latLng.lng());
    },
    updateMap(latitude, longitude, address) {
      this.setLatLng(latitude, longitude);
      this.formData.address = { ...this.formData.address, ...address };
      this.mapZoom = 16;
    },
    parseGeocodeAddress(result) {
      let street_number = result.filter((item) => item.types.includes('street_number'))
      street_number = street_number[0] ? street_number[0].long_name : ''

      let address = result.filter((item) => item.types.includes('route'))
      address = address[0] ? address[0].short_name : ''

      let locality = result.filter((item) => item.types.includes('administrative_area_level_2'))
      locality = locality[0] ? locality[0].long_name : ''

      let sublocality = result.filter(
          (item) =>
              item.types.includes('sublocality') ||
              item.types.includes('sublocality_level_1') ||
              item.types.includes('administrative_area_level_4')
      )
      sublocality = sublocality[0] ? sublocality[0].long_name : ''

      let administrative_area = result.filter((item) => item.types.includes('administrative_area_level_1'))
      administrative_area = administrative_area[0] ? administrative_area[0].short_name : ''

      let postal_code = result.filter((item) => item.types.includes('postal_code'))
      postal_code = postal_code[0] ? postal_code[0].long_name : ''

      return {
        street_number,
        address,
        locality,
        sublocality,
        administrative_area,
        postal_code
      }
    },
    showNotification(message, color) {
      this.snackbar.text = message;
      this.snackbar.show = true;
      this.snackbar.color = color;
    },
    setLatLng(lat, lng) {
      if (typeof lat === 'number' && typeof lng === 'number') {
        this.formData.latitude = lat;
        this.formData.longitude = lng;
      }
    },
    changeScheduleDate(data) {
      this.formData.schedule_start = data.schedule_start;
      this.formData.schedule_start_at = data.schedule_start_at;
      this.formData.schedule_end = data.schedule_end;
      this.formData.schedule_end_at = data.schedule_end_at;
    },
    async callbackGeocode(search) {
      const result = await this.$store.dispatch('locations/geocodeSearch', {
        key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
        address: search
      })
      return result.map(item => ({ title: item.full_text, value: item.title, ...item }))
    }
  },
  watch: {
    location(location) {
      if (!this.bulkUpdate) {
        this.setData(location)
      }
    },
    open(open) {
      if (open && !this.bulkUpdate) {
        this.setData(this.location)
      }
    },
    geocodeAddressesError(e) {
      this.loadingGeocode = false
      console.error(e)
      this.showNotification(e,'red darken-2')
    },
    'formData.address.address_line_one': function(data) {
      if (data) this.removeAddress = false;
    },
    'formData.address.administrative_area': function(data) {
      if (data) this.removeAddress = false;
    },
    'formData.address.locality': function(data) {
      if (data) this.removeAddress = false;
    },
    'formData.address.postal_code': function(data) {
      if (data) this.removeAddress = false;
    },
    'formData.address.sublocality': function(data) {
      if (data) this.removeAddress = false;
    }
  }
};
</script>

<style scoped></style>
